import { Delete } from "@mui/icons-material";
import { Button, styled } from "@mui/material";
import { debounce, get } from "lodash";
import { useEffect, useRef } from "react";
import { commencementDateTypes } from "../../../app/js/types/commencementDateTypes";
import { propertyManagerTypes } from "../../../app/js/types/propertyManagerTypes";
import Autocomplete from "../../../common/js/components/Autocomplete";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import { Add, Search } from "../../../common/js/icons";
import searchConstants from "../search/constants";

const StyledDefTerm = styled("span")({
    fontSize: "13px",
    textTransform: "uppercase",
    color: "#9E9E9E",
    fontWeight: 600,
    display: "inline-block",
    padding: "0 10px 3px",
});

const StyledManagementInfoContainerRow = styled("div")({
    padding: "5px 5px",
});

const StyledMoveItButton = styled(Button)({
    margin: "10px 0 10px 0px",
    paddingTop: "8px",
    "&:hover": {
        background: "transparent",
    },
});

const StyledFlexContainer = styled("div")({
    display: "flex",
    justifyContent: "center",
    width: "100%",
    marginLeft: "20px",
});

const StyledFlexItem = styled("div")({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
});

const StyledCommencementWrapper = styled("div")({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "8px 0px",
});

const StyledManagementContainer = styled("div")({
    border: "1px solid #dbdbdb",
    display: "flex",
    flexDirection: "column",
    height: "100%",
});

const PropertyManagementInformation = (props) => {
    const { autocompleteResults, data, disableInputs, handleInput, personnelInfo, searchKMPerson } = props;

    const scrollContainerRef = useRef(null);
    const enableScrollToBottom = useRef(false);

    useEffect(() => {
        if (enableScrollToBottom.current && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
            enableScrollToBottom.current = false;
        }
    }, [personnelInfo]);

    const updatePersonnelRole = (roleIndex, newRole) => {
        const updatedPersonnel = personnelInfo.map((person, index) =>
            index === roleIndex ? { ...person, role: newRole } : person
        );
        handleInput("personnelInfo", updatedPersonnel);
    };

    const deletePersonnel = (roleIndex) => {
        handleInput(
            "personnelInfo",
            personnelInfo.filter((item, index) => index !== roleIndex)
        );
    };

    const addNewPersonnel = () => {
        enableScrollToBottom.current = true;
        handleInput("personnelInfo", [
            ...personnelInfo,
            { propertyManagementKey: data?.propertyManagementKey, role: "Select" },
        ]);
    };

    const isAddPersonnelValid = () => {
        return personnelInfo && personnelInfo.some((personnel) => personnel.role === "Select");
    };

    const getPersonnelName = (personnelItem) => {
        return personnelItem.person
            ? get(personnelItem, `person.firstName`, "") + " " + get(personnelItem, `person.lastName`, "")
            : "";
    };

    const addPersonToPersonnel = (roleIndex, person) => {
        const updatedPersonnel = personnelInfo.map((personnelItem, index) =>
            index === roleIndex ? { ...personnelItem, person: person } : personnelItem
        );
        handleInput("personnelInfo", updatedPersonnel);
    };

    const debounceSearchPersonnel = debounce((term) => {
        searchKMPerson(term);
    }, searchConstants["DEBOUNCE_SEARCH_DELAY"]);

    return (
        <StyledManagementContainer>
            <div style={{ marginLeft: "8px" }}>
                <h3 style={{ margin: "22px 0px 14px 0px" }}>Management Information</h3>
            </div>

            <StyledCommencementWrapper>
                <div>
                    <StyledDefTerm>Commencement Date:</StyledDefTerm>
                </div>
                <div>
                    <BSInput
                        id="commencementDate"
                        value={data.commencementDate}
                        placeholder="Enter date"
                        errorPlaceholder="hideIt"
                        onChange={(e) => handleInput(e.target.id, e.target.value)}
                        disabled={disableInputs}
                        noFullWidth={true}
                        type="date"
                        noHelperText={true}
                        extraFormControlStyles={{ marginRight: "8px", marginBottom: "0px" }}
                        extraInputStyles={{ "--bsinput-input-mt": "0px" }}
                    />
                </div>
                <div>
                    <BSSelect
                        id="commencementDateType"
                        value={data.commencementDateType}
                        placeholder="Effective Date"
                        onChange={(e) => handleInput(e.target.id, e.target.value)}
                        combinedField="labelLessShiftLeft"
                        disabled={disableInputs}
                        noHelperText={true}
                        options={[
                            {
                                value: "",
                                label: "Select",
                                disabled: true,
                            },
                            ...commencementDateTypes,
                        ]}
                        extraFormControlStyles={{ minWidth: "180px", marginBottom: "0px" }}
                        extraInputStyles={{ "--bsselect-input-mt": "0px" }}
                    />
                </div>
            </StyledCommencementWrapper>
            <div
                ref={scrollContainerRef}
                style={{
                    maxHeight: "220px",
                    overflowX: "hidden",
                    overflowY: "auto",
                }}
            >
                {personnelInfo &&
                    personnelInfo.map((element, index) => (
                        <StyledManagementInfoContainerRow key={`Personnel-${index}`}>
                            <StyledFlexContainer>
                                <StyledFlexItem>
                                    <div>
                                        <Autocomplete
                                            id="name"
                                            extraFormControlStyles={{ marginBottom: "-1px", marginRight: "8px" }}
                                            initialInputValue={getPersonnelName(element)}
                                            itemToString={(i) => (i == null ? "" : i.firstName + " " + i.lastName)}
                                            onSelect={(person) => addPersonToPersonnel(index, person)}
                                            options={{
                                                key: "person",
                                                display: (suggestion) => {
                                                    return suggestion.firstName + " " + suggestion.lastName;
                                                },
                                            }}
                                            placeholder="Enter name"
                                            searchFunction={(input) => debounceSearchPersonnel(input)}
                                            startAdornment={<Search />}
                                            suggestions={autocompleteResults}
                                            disabled={disableInputs}
                                        />
                                    </div>
                                    <div>
                                        <BSSelect
                                            id="role"
                                            value={element.role === "Select" ? "" : element.role}
                                            placeholder="Select"
                                            onChange={(e) => updatePersonnelRole(index, e.target.value)}
                                            combinedField="labelLessShiftLeft"
                                            options={[
                                                {
                                                    value: "",
                                                    label: "Select",
                                                    disabled: true,
                                                },
                                                ...propertyManagerTypes,
                                            ]}
                                            extraFormControlStyles={{ marginBottom: "0px", marginLeft: "8px" }}
                                            extraInputStyles={{ "--bsselect-input-mt": "0px" }}
                                        />
                                    </div>
                                    <div>
                                        <Button
                                            onClick={() => {
                                                deletePersonnel(index);
                                            }}
                                        >
                                            <Delete />
                                        </Button>
                                    </div>
                                </StyledFlexItem>
                            </StyledFlexContainer>
                        </StyledManagementInfoContainerRow>
                    ))}
            </div>
            <div style={{ marginTop: "auto" }}>
                {disableInputs !== true && (
                    <StyledMoveItButton
                        color="primary"
                        onClick={() => addNewPersonnel()}
                        disabled={isAddPersonnelValid()}
                    >
                        <Add fill={isAddPersonnelValid() ? "#DCDCDC" : "#4b94c1"} /> Add Personnel
                    </StyledMoveItButton>
                )}
            </div>
        </StyledManagementContainer>
    );
};

export default PropertyManagementInformation;
